import { useLocation } from 'react-router-dom';
import { useMemo, useState, useEffect } from 'react';
import { getSupabaseClient } from '../supabaseClient';
import { useBusinessType } from '../context/BusinessContext';

// Page context mapping - defines what context is available for each page
const PAGE_CONTEXT_MAP = {
  '/home': {
    pageName: 'Dashboard',
    category: 'Monitor',
    description: 'Main dashboard showing calls, solved rate, and tasks overview',
    availableActions: [
      {
        name: 'Time Filter',
        description: 'Switch between day, week, and month views',
        triggers: ['time period', 'filter', 'day', 'week', 'month', 'calendar']
      },
      {
        name: 'Call Testing',
        description: 'Test AI by making a call to your business number',
        triggers: ['test call', 'try ai', 'call test', 'phone test']
      },
      {
        name: 'Metrics Cards',
        description: 'View detailed breakdowns of calls, solved rate, and tasks',
        triggers: ['expand', 'details', 'metrics', 'breakdown', 'advanced view']
      }
    ]
  },

  '/activity': {
    pageName: 'Activity',
    category: 'Monitor', 
    description: 'View and manage all calls and conversations',
    availableActions: [
      {
        name: 'Call Management',
        description: 'View call transcripts, listen to recordings, see customer names and phone numbers. Action items from calls will be displayed here too.',
        triggers: ['call details', 'transcript', 'recording', 'listen', 'audio', 'action items', 'customer name', 'phone number']
      },
      {
        name: 'Text Messaging',
        description: 'View text conversations and reply directly to customers from this page. Customer names and numbers are shown for each conversation.',
        triggers: ['text back', 'reply', 'message', 'text customer', 'respond', 'conversation', 'sms']
      },
      {
        name: 'Call Filtering',
        description: 'Filter calls by status, date, or type',
        triggers: ['filter calls', 'search calls', 'find call', 'call history']
      },
      {
        name: 'Activity Overview',
        description: 'See all customer interactions in one place with names, numbers, and action items',
        triggers: ['overview', 'all activity', 'customer info', 'contact details']
      }
    ]
  },

  '/monitor/analysis': {
    pageName: 'Analysis',
    category: 'Monitor',
    description: 'Analyze call patterns, performance metrics, and business insights',
    availableActions: [
      {
        name: 'Performance Analytics',
        description: 'View AI performance and success rates',
        triggers: ['analytics', 'performance', 'success rate', 'metrics']
      }
    ]
  },

  '/train/brainv2': {
    pageName: 'Brain Training',
    category: 'Train',
    description: 'Configure AI responses, actions, and knowledge base',
    availableActions: [
      {
        name: 'Simple Answer',
        description: 'Create quick Q&A responses for common questions',
        triggers: ['simple answer', 'quick response', 'qa', 'question answer']
      },
      {
        name: 'Complex Answer',
        description: 'Create detailed responses with rich formatting',
        triggers: ['complex answer', 'detailed response', 'formatted answer']
      },
      {
        name: 'Simple Action',
        description: 'Set up automated actions like SMS, email, or transfers',
        triggers: ['simple action', 'automation', 'sms', 'email', 'transfer']
      },
      {
        name: 'Complex Action',
        description: 'Create multi-step workflows and order pathways',
        triggers: ['complex action', 'workflow', 'pathway', 'multi-step']
      },
      {
        name: 'Category Management',
        description: 'Organize brain settings into categories',
        triggers: ['category', 'organize', 'group settings']
      }
    ]
  },

  '/train/voice': {
    pageName: 'Voice Settings',
    category: 'Train',
    description: 'Configure AI voice, personality, and call behavior',
    availableActions: [
      {
        name: 'Voice Selection',
        description: 'Choose AI voice and speaking style',
        triggers: ['voice', 'speaking style', 'personality', 'tone']
      },
      {
        name: 'Language Support',
        description: 'Available languages: English, Spanish, Portuguese, Mandarin, and French. The team is planning to add more languages in the future.',
        triggers: ['language', 'languages', 'spanish', 'portuguese', 'mandarin', 'french', 'multilingual', 'what languages']
      },
      {
        name: 'Call Behavior',
        description: 'Set how AI handles different call scenarios',
        triggers: ['call behavior', 'handling', 'scenarios', 'responses']
      }
    ]
  },

  '/train/places': {
    pageName: 'Location Priority',
    category: 'Train',
    description: 'Manage location-specific settings and priorities',
    availableActions: [
      {
        name: 'Priority Settings',
        description: 'Set location priority and routing rules',
        triggers: ['priority', 'location routing', 'preferences']
      }
    ]
  },

  '/connect/apps': {
    pageName: 'App Integrations',
    category: 'Connect',
    description: 'Connect external apps and services',
    availableActions: [
      {
        name: 'Integration Setup',
        description: 'Connect with third-party services',
        triggers: ['integration', 'connect app', 'third party', 'external']
      }
    ]
  },

  '/connect/setup': {
    pageName: 'Phone Setup',
    category: 'Connect',
    description: 'Configure phone system integration and routing',
    availableActions: [
      {
        name: 'Phone Configuration',
        description: 'Set up phone numbers and routing',
        triggers: ['phone setup', 'number', 'routing', 'forwarding']
      }
    ]
  },

  '/connect/alerts': {
    pageName: 'Email Preferences',
    category: 'Connect',
    description: 'Manage email notifications and alert settings',
    availableActions: [
      {
        name: 'Email Settings',
        description: 'Configure notification preferences',
        triggers: ['email', 'notifications', 'alerts', 'preferences']
      }
    ]
  },

  '/account/general': {
    pageName: 'Account Settings',
    category: 'Account',
    description: 'Manage account information and general settings',
    availableActions: [
      {
        name: 'Profile Management',
        description: 'Update account details and preferences',
        triggers: ['profile', 'account', 'settings', 'preferences']
      }
    ]
  },

  // Onboarding Pages
  '/onboarding/intro': {
    pageName: 'Business Setup',
    category: 'Onboarding',
    description: 'Initial business information setup to configure your AI assistant',
    availableActions: [
      {
        name: 'Business Type Selection',
        description: 'Choose your business type from a dropdown (helps AI understand your industry language). All changes save automatically.',
        triggers: ['business type', 'industry', 'what business', 'type of business', 'laundromat', 'restaurant', 'retail']
      },
      {
        name: 'Services Offered',
        description: 'Select all services your business provides. Options appear after choosing business type.',
        triggers: ['services', 'what services', 'offerings', 'what do you offer', 'service types']
      },
      {
        name: 'Business Name',
        description: 'Enter your business name - this is what your AI will call your business when talking to customers.',
        triggers: ['business name', 'company name', 'what to call business', 'business title']
      },
      {
        name: 'Website Verification',
        description: 'Enter your website URL and it will be automatically verified to ensure it works. Your AI can confidently direct customers to it.',
        triggers: ['website', 'url', 'web address', 'online', 'site verification']
      },
      {
        name: 'Area Code Selection',
        description: 'Enter your preferred 3-digit area code and the system will check for available phone numbers in that area.',
        triggers: ['area code', 'phone number', 'local number', 'telephone', 'phone availability']
      },
      {
        name: 'Phone Number Requirements',
        description: 'A dedicated AI phone line is REQUIRED for the system to work. If you have existing numbers on your website/Google, you have two options: 1) Update those listings to use the new AI number, or 2) Keep your existing published numbers and forward all calls to the dedicated AI line.',
        triggers: ['why phone number', 'existing number', 'current phone', 'keep my number', 'phone forwarding', 'dedicated line', 'new phone number', 'phone requirements', 'required phone']
      },
      {
        name: 'Progress Tracking',
        description: 'See completion progress (X of 5 fields complete). All information saves automatically as you type.',
        triggers: ['progress', 'how many left', 'completion', 'almost done', 'save progress']
      }
    ]
  },

  '/onboarding/business': {
    pageName: 'Location Setup',
    category: 'Onboarding',
    description: 'Add and configure your business locations for accurate customer assistance',
    availableActions: [
      {
        name: 'Add Locations',
        description: 'Add your business locations with Google address verification. Click "Add Location" button to start.',
        triggers: ['add location', 'new location', 'business address', 'where located', 'multiple locations']
      },
      {
        name: 'Google Verification',
        description: 'Verify addresses with Google Maps to ensure accurate directions for customers.',
        triggers: ['verify address', 'google maps', 'address verification', 'accurate directions']
      },
      {
        name: 'Location Details',
        description: 'For each location, add alternative names (like "downtown location"), nearby landmarks, and major roads for better customer understanding.',
        triggers: ['location details', 'alternative names', 'landmarks', 'nearby roads', 'location info']
      },
      {
        name: 'Location Priority',
        description: 'Organize locations by importance - your main location should be first. AI mentions higher priority locations first.',
        triggers: ['location priority', 'main location', 'primary location', 'location order', 'most important']
      },
      {
        name: 'Maps Integration',
        description: 'View all your locations on an interactive map to visualize their distribution.',
        triggers: ['map view', 'see on map', 'location map', 'visual map', 'where locations']
      }
    ]
  },

  '/onboarding/notifications': {
    pageName: 'Email Notifications',
    category: 'Onboarding',
    description: 'Set up email notifications to receive call insights and stay connected to customer conversations',
    availableActions: [
      {
        name: 'Add Email Addresses',
        description: 'Add email addresses for team members, managers, or partners who need call notifications. Click "Add Email" to start.',
        triggers: ['add email', 'email address', 'notifications', 'who gets notified', 'team emails']
      },
      {
        name: 'Notification Types',
        description: 'Choose between Simple (key highlights and action items) or Advanced (complete record with full conversation and audio) notification types.',
        triggers: ['notification type', 'simple vs advanced', 'what notifications', 'email content', 'call summaries']
      },
      {
        name: 'Email Preferences',
        description: 'Configure what types of calls trigger notifications and customize email content preferences.',
        triggers: ['email preferences', 'notification settings', 'when to notify', 'call alerts']
      },
      {
        name: 'Complete Setup',
        description: 'After adding at least one email, you can complete the onboarding process and start using your AI assistant.',
        triggers: ['complete setup', 'finish onboarding', 'ready to launch', 'done with setup', 'start using ai']
      },
      {
        name: 'Call Insights',
        description: 'Get summaries of every customer call so you never miss important opportunities or issues.',
        triggers: ['call insights', 'call summaries', 'what happened', 'customer conversations', 'call reports']
      }
    ]
  },

  // Additional onboarding route mappings
  '/intro/locations': {
    pageName: 'Location Setup',
    category: 'Onboarding',
    description: 'Add and configure your business locations for accurate customer assistance',
    availableActions: [
      {
        name: 'Add Locations',
        description: 'Add your business locations with Google address verification. Click "Add Location" button to start.',
        triggers: ['add location', 'new location', 'business address', 'where located', 'multiple locations']
      },
      {
        name: 'Google Verification',
        description: 'Verify addresses with Google Maps to ensure accurate directions for customers.',
        triggers: ['verify address', 'google maps', 'address verification', 'accurate directions']
      },
      {
        name: 'Location Details',
        description: 'For each location, add alternative names (like "downtown location"), nearby landmarks, and major roads for better customer understanding.',
        triggers: ['location details', 'alternative names', 'landmarks', 'nearby roads', 'location info']
      },
      {
        name: 'Location Priority',
        description: 'Organize locations by importance - your main location should be first. AI mentions higher priority locations first.',
        triggers: ['location priority', 'main location', 'primary location', 'location order', 'most important']
      },
      {
        name: 'Maps Integration',
        description: 'View all your locations on an interactive map to visualize their distribution.',
        triggers: ['map view', 'see on map', 'location map', 'visual map', 'where locations']
      }
    ]
  }
};

// Create a simple event emitter for brain settings updates
let brainSettingsListeners = [];

export const refreshBrainSettings = () => {
  brainSettingsListeners.forEach(callback => callback());
};

export const usePageContext = () => {
  const location = useLocation();
  const { businessType } = useBusinessType();
  const supabase = getSupabaseClient(businessType);
  const [brainSettings, setBrainSettings] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Listen for brain settings refresh events
  useEffect(() => {
    const refreshCallback = () => {
      setRefreshTrigger(prev => prev + 1);
    };

    brainSettingsListeners.push(refreshCallback);

    return () => {
      brainSettingsListeners = brainSettingsListeners.filter(cb => cb !== refreshCallback);
    };
  }, []);

  // Fetch brain settings when on brain page
  useEffect(() => {
    const fetchBrainSettings = async () => {
      if (location.pathname !== '/train/brainv2') {
        setBrainSettings([]);
        return;
      }

      try {
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError) return;

        // Fetch categories with their settings
        const { data: categoriesData, error: categoriesError } = await supabase
          .from('brain_categories')
          .select('*')
          .eq('account_id', user.id)
          .order('name');

        if (categoriesError) return;

        const categoriesWithSettings = await Promise.all(
          categoriesData.map(async (category) => {
            const { data: settingsData, error: settingsError } = await supabase
              .from('brain_settings')
              .select('*')
              .eq('category_id', category.id);

            if (settingsError) {
              return { ...category, settings: [] };
            }

            // Fetch additional details for each setting
            const enhancedSettings = await Promise.all(
              (settingsData || []).map(async (setting) => {
                let additionalData = {};

                if (setting.type === 'simple_answer') {
                  const { data: answerData } = await supabase
                    .from('brain_simple_answers')
                    .select('content, title')
                    .eq('settings_id', setting.id)
                    .maybeSingle();

                  if (answerData) {
                    additionalData = {
                      content: answerData.content,
                      title: answerData.title
                    };
                  }
                } else if (setting.type === 'simple_action') {
                  const { data: actionData } = await supabase
                    .from('brain_simple_actions')
                    .select('action_type')
                    .eq('settings_id', setting.id)
                    .maybeSingle();

                  if (actionData) {
                    additionalData = {
                      action_type: actionData.action_type
                    };
                  }
                }

                return {
                  ...setting,
                  ...additionalData
                };
              })
            );

            return { ...category, settings: enhancedSettings };
          })
        );

        setBrainSettings(categoriesWithSettings);
      } catch (error) {
        console.error('Error fetching brain settings:', error);
      }
    };

    fetchBrainSettings();
  }, [location.pathname, supabase, refreshTrigger]);

  const pageContext = useMemo(() => {
    const currentPath = location.pathname;
    const context = PAGE_CONTEXT_MAP[currentPath];

    if (!context) {
      return {
        pageName: 'Unknown Page',
        category: 'General',
        description: 'Current page context not configured',
        availableActions: [],
        contextPrompt: ''
      };
    }

    // Generate context prompt for brain injection
    const contextPrompt = generateContextPrompt(context, currentPath === '/train/brainv2' ? brainSettings : []);

    return {
      ...context,
      currentPath,
      contextPrompt,
      brainSettings
    };
  }, [location.pathname, brainSettings]);

  return pageContext;
};

const generateContextPrompt = (context, brainSettings = []) => {
  const { pageName, category, description, availableActions } = context;

  let prompt = `\n--- CURRENT PAGE CONTEXT ---\n`;
  prompt += `Page: ${pageName} (${category} section)\n`;
  prompt += `Description: ${description}\n`;

  if (availableActions.length > 0) {
    prompt += `\nAvailable Actions on this page:\n`;
    availableActions.forEach(action => {
      prompt += `• ${action.name}: ${action.description}\n`;
      if (action.triggers.length > 0) {
        prompt += `  Triggers: ${action.triggers.join(', ')}\n`;
      }
    });
  }

  // Add brain settings information if available
  if (brainSettings.length > 0) {
    prompt += `\n--- CURRENT BRAIN SETTINGS ---\n`;
    prompt += `The user has configured the following AI knowledge:\n\n`;

    brainSettings.forEach(category => {
      if (category.settings.length > 0) {
        prompt += `Category: "${category.name}"\n`;
        if (category.description) {
          prompt += `  Category Description: ${category.description}\n`;
        }

        category.settings.forEach(setting => {
          prompt += `\n  Setting: "${setting.name}"\n`;
          prompt += `    Type: ${setting.type}\n`;
          if (setting.description) {
            prompt += `    Description: ${setting.description}\n`;
          }
          if (setting.triggers && setting.triggers.length > 0) {
            prompt += `    Triggers: ${setting.triggers.join(', ')}\n`;
          }

          // Add type-specific information
          if (setting.type === 'simple_answer' && setting.content) {
            prompt += `    Content: ${setting.content.substring(0, 200)}${setting.content.length > 200 ? '...' : ''}\n`;
          } else if (setting.type === 'simple_action' && setting.action_type) {
            prompt += `    Action Type: ${setting.action_type}\n`;
          }
        });
        prompt += `\n`;
      }
    });

    prompt += `This shows what the AI currently knows. Use this information when helping users understand their current AI configuration.\n`;
  }

  prompt += `\nUse this context to provide relevant assistance about the current page features.\n`;

  // Add technical support contact information
  prompt += `\n--- TECHNICAL SUPPORT ---\n`;
  prompt += `If you cannot help the user or they need human/technical assistance, direct them to contact: caleb@thesavants.ai\n`;
  prompt += `This is Caleb, co-founder and CTO of The Savants. Only mention his expertise (world's leading expert in AI for Laundromats and Dry Cleaners) if specifically asked.\n`;

  prompt += `--- END PAGE CONTEXT ---\n`;

  return prompt;
}; 